import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Grid from '@mui/system/Unstable_Grid';
import Link from '@mui/material/Link';

const FileUpload = ({ helpText, id, onChange, uploadedFiles, disabled }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [error, setError] = useState('');

    const MAX_TOTAL_SIZE = 2 * 1024 * 1024 * 1024; // 2 GB in bytes

    const handleChange = (event) => {
        const files = Array.from(event.target.files);
        const totalSize = files.reduce((sum, file) => sum + file.size, 0);

        if (totalSize > MAX_TOTAL_SIZE) {
            setSelectedFiles([]);
            setError('Лимит на качване на файлове до 2GB.');
        } else {
            setSelectedFiles(files);
            setError('');
            if (onChange) {
                onChange(files);
            }
        }
    };

    const getFilenameFromUrl = (url) => {
        try {
            const pathname = new URL(url, window.location.origin).pathname;
            const name = pathname.split('/').pop();
            return name ? decodeURIComponent(name) : url;
        } catch {
            return url.split('/').pop() || url;
        }
    };

    const handleDownload = async (event, url) => {
        event.preventDefault();
        const filename = getFilenameFromUrl(url);
        try {
            const response = await fetch(url, { mode: 'cors' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            // Fallback: open in a new tab if cross-origin/CORS blocks the fetch.
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <Grid container>
            <Grid item>
                <Grid container>
                    {/* Hidden file input */}
                    <input
                        accept="*"
                        id={id}
                        type="file"
                        multiple
                        disabled={disabled}
                        style={{ display: 'none' }}
                        onChange={handleChange}
                    />
                    <label htmlFor={id}>
                        <IconButton component="span" disabled={disabled}>
                            <UploadFileIcon fontSize='large' />
                        </IconButton>
                    </label>
                    <Typography sx={{ mt: 2 }}>{helpText}</Typography>
                </Grid>
            </Grid>
            <Grid item>
                {/* Show error if limit exceeded */}
                {error && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {error}
                    </Typography>
                )}

                {/* Show selected file names */}
                {
                    (uploadedFiles || selectedFiles.length > 0) &&
                    (
                        <List dense>
                            {selectedFiles.length == 0 ?
                                uploadedFiles.map((file, index) => (
                                    <ListItem key={index}>
                                        <Link
                                            href={file}
                                            onClick={(event) => handleDownload(event, file)}
                                            download={getFilenameFromUrl(file)}
                                            rel="noopener noreferrer"
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <ListItemText
                                                primary={getFilenameFromUrl(file)}
                                            />
                                        </Link>
                                    </ListItem>
                                ))
                                : selectedFiles.length > 0 && selectedFiles.map((file, index) => (
                                    <ListItem key={index}>
                                        <ListItemText
                                            primary={file.name}
                                            secondary={`${(file.size / (1024 * 1024)).toFixed(2)} MB`}
                                        />
                                    </ListItem>
                                ))
                            }
                        </List>
                    )
                }
            </Grid>
        </Grid>
    );
}

export default FileUpload;
